import { useCallback, useEffect, useState } from 'react';
import api from '../utils/api.util';
import toast from '../utils/toast.util';
import { useAuth } from '../auth/AuthContext';
import { ENDPOINT } from '../constants/common-app.const';
import { CourseTopic, CourseTopicStatus } from '../pages/Course/Course.const';

interface ContractDetail {
  id: string;
  courseId: string;
  status: string;
  course: { id: string; name: string; suffix?: string } | null;
  listeners: Array<{ id: string; listenerUid: string; userId: string | null }>;
}

const useGetContractWithProgress = ({ contractId }: { contractId: string }) => {
  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [themes, setThemes] = useState<CourseTopic[]>([]);
  const [listenerUid, setListenerUid] = useState<string | null>(null);
  const [completedThemeIds, setCompletedThemeIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const contractData: ContractDetail = await api.getJson(
        `${ENDPOINT.CONTRACTS}/${contractId}`
      );
      setContract(contractData);

      const rawThemes: any[] = await api.get(
        `${ENDPOINT.COURSES}/${contractData.courseId}/themes`
      );
      const sorted = [...(rawThemes ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder
      );

      setListenerUid((contractData as any).myListenerUid ?? null);

      const topicList: CourseTopic[] = sorted.map((t: any) => ({
        id: t.id,
        order: t.displayOrder,
        title: t.name,
        durationMinutes: 0,
        status: 'locked' as CourseTopicStatus,
        isOpen: Boolean(t.openForView),
      }));
      setThemes(topicList);
      // TODO(CR-02): seed completedThemeIds from backend once
      // GET /api/contracts/:contractId/listeners/:listenerUid/themes/completed
      // is available. Without this, progress resets on every page load.
      setLoading(false);
    } catch (err) {
      toast.showOnFailure('Ошибка загрузки данных курса');
      setError('fetch failed');
      setLoading(false);
    }
  }, [contractId, user?.uid]);

  useEffect(() => {
    if (isAuthenticated && contractId) {
      fetchAll();
    }
  }, [isAuthenticated, contractId, fetchAll]);

  const markThemeCompleted = (themeId: string) => {
    setCompletedThemeIds(prev => new Set([...Array.from(prev), themeId]));
  };

  return { contract, themes, listenerUid, completedThemeIds, markThemeCompleted, loading, error };
};

export default useGetContractWithProgress;
